import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Clock, MapPin, Globe, CheckCircle, ChevronRight,
  GraduationCap, CreditCard, Users, BookOpen, Target,
} from 'lucide-react';

const modeLabel = { presentiel: 'Presentiel', distance: 'A distance', hybride: 'Hybride' };

const TrainingPreview = ({ form, categories = [], instructors = [] }) => {
  const category = categories.find(c => c.id === form.category_id);
  const selectedInstructors = instructors.filter(i => (form.instructor_ids || []).includes(i.id));
  const objectives = typeof form.objectives === 'string'
    ? form.objectives.split('\n').filter(Boolean)
    : (form.objectives || []);
  const price = form.promo_price ? parseFloat(form.promo_price) : parseFloat(form.price) || 0;
  const outline = form.outline || [];

  const hasContent = form.title || form.short_description || objectives.length > 0 || outline.length > 0;

  if (!hasContent) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8" data-testid="preview-empty">
        <GraduationCap className="w-12 h-12 text-neutral-200 mb-3" />
        <p className="text-neutral-400 text-sm">Commencez a remplir le formulaire pour voir l'apercu en temps reel.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0 text-sm" data-testid="training-preview">
      {/* Mini Hero */}
      <div className="bg-[#1D1D1B] rounded-t-lg p-5 relative overflow-hidden">
        {form.image_url && (
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url("${form.image_url}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        )}
        <div className="relative z-10">
          {category && (
            <Badge className="mb-2 text-[10px] text-[#1D1D1B] px-1.5 py-0" style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}>
              {category.name}
            </Badge>
          )}
          <h2 className="font-heading text-lg font-bold text-white mb-1 leading-tight">
            {form.title || 'Titre de la formation'}
          </h2>
          {form.short_description && (
            <p className="text-white/60 text-xs leading-relaxed">{form.short_description}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-white/50">
            {form.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{form.duration}</span>}
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{modeLabel[form.mode] || form.mode}</span>
            {form.language && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{form.language}</span>}
          </div>
        </div>
      </div>

      {/* Price Bar */}
      <div className="flex items-center justify-between px-5 py-3" style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.1) 0%, rgba(249,180,56,0.1) 100%)' }}>
        <div className="flex items-center gap-2">
          {form.promo_price ? (
            <>
              <span className="text-lg font-bold text-[#F9B438]">{parseFloat(form.promo_price).toFixed(0)} $</span>
              <span className="text-xs text-neutral-400 line-through">{parseFloat(form.price).toFixed(0)} $</span>
            </>
          ) : price > 0 ? (
            <span className="text-lg font-bold text-[#F9B438]">{price.toFixed(0)} $</span>
          ) : (
            <span className="text-lg font-bold text-green-500">Gratuit</span>
          )}
          <span className="text-[10px] text-neutral-400">/ participant</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-neutral-400">
          <CreditCard className="w-3 h-3" /> Stripe
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Description */}
        {form.full_description && (
          <div>
            <h3 className="font-heading text-xs font-bold text-[#1D1D1B] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-[#F9B438]" />Description
            </h3>
            <p className="text-neutral-600 text-xs leading-relaxed whitespace-pre-line">{form.full_description}</p>
          </div>
        )}

        {/* Objectives */}
        {objectives.length > 0 && (
          <div>
            <h3 className="font-heading text-xs font-bold text-[#1D1D1B] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-[#F9B438]" />Objectifs
            </h3>
            <ul className="space-y-1.5">
              {objectives.map((obj, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-[#F9B438] mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-600 text-xs">{obj}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Outline / Programme */}
        {outline.length > 0 && (
          <div>
            <h3 className="font-heading text-xs font-bold text-[#1D1D1B] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-[#F9B438]" />Programme
            </h3>
            <div className="space-y-2">
              {outline.map((section, i) => (
                <Card key={i} className="border shadow-none">
                  <CardContent className="p-3">
                    <h4 className="font-medium text-[#1D1D1B] text-xs mb-2 flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-[#1D1D1B] flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}>
                        {i + 1}
                      </span>
                      {section.title || <span className="italic text-neutral-300">Sans titre</span>}
                    </h4>
                    {section.items?.length > 0 && (
                      <ul className="space-y-1 ml-6">
                        {section.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-1.5 text-[11px] text-neutral-500">
                            <ChevronRight className="w-3 h-3 text-[#F9B438] mt-0.5 flex-shrink-0" />
                            <div>
                              <span>{item.title || <span className="italic text-neutral-300">Module sans titre</span>}</span>
                              {item.duration && <span className="text-neutral-300 ml-1">({item.duration})</span>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Sidebar info */}
        <div className="grid grid-cols-2 gap-2">
          {form.target_audience && (
            <div className="rounded-lg border p-2.5">
              <p className="text-[10px] font-medium text-neutral-400 flex items-center gap-1 mb-1"><Users className="w-3 h-3 text-[#F9B438]" />Public cible</p>
              <p className="text-xs text-neutral-600">{form.target_audience}</p>
            </div>
          )}
          {form.prerequisites && (
            <div className="rounded-lg border p-2.5">
              <p className="text-[10px] font-medium text-neutral-400 flex items-center gap-1 mb-1"><BookOpen className="w-3 h-3 text-[#F9B438]" />Prerequis</p>
              <p className="text-xs text-neutral-600">{form.prerequisites}</p>
            </div>
          )}
        </div>

        {/* Instructors */}
        {selectedInstructors.length > 0 && (
          <div>
            <h3 className="font-heading text-xs font-bold text-[#1D1D1B] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-[#F9B438]" />Formateurs
            </h3>
            <div className="space-y-2">
              {selectedInstructors.map(inst => (
                <div key={inst.id} className="flex items-center gap-2">
                  {inst.photo_url ? (
                    <img src={inst.photo_url} alt={inst.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-neutral-100 flex items-center justify-center">
                      <Users className="w-4 h-4 text-neutral-300" />
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-[#1D1D1B]">{inst.name}</p>
                    {inst.title && <p className="text-[10px] text-neutral-400">{inst.title}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingPreview;
